import React, { createContext, useContext, useState, ReactNode } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase/client";

interface Plan {
  id?: string;
  name: string;
  price: number;
  timestamp: number;
}

interface PromoCode {
  id: string;
  code: string;
  discount: number;
  discount_type: "percentage" | "fixed";
  start_date: string;
  end_date: string;
  max_uses: number | null;
  current_uses: number;
  min_purchase_amount: number | null;
  is_active: boolean;
}

interface CartContextType {
  selectedPlan: Plan | null;
  setSelectedPlan: (plan: Plan | null) => void;
  clearCart: () => void;
  initiatePayment: () => Promise<{ success: boolean; error?: unknown }>;
  isProcessingPayment: boolean;
  applyPromoCode: (
    code: string
  ) => Promise<{ success: boolean; message: string }>;
  removePromoCode: () => void;
  promoCode: PromoCode | null;
  discount: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(() => {
    const storedPlan = localStorage.getItem("selectedPlan");
    if (storedPlan) {
      try {
        const plan = JSON.parse(storedPlan);
        if (Date.now() - plan.timestamp < 30 * 60 * 1000) {
          return plan;
        }
        localStorage.removeItem("selectedPlan");
      } catch (error) {
        console.error("Error parsing stored plan:", error);
        localStorage.removeItem("selectedPlan");
      }
    }
    return null;
  });

  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [promoCode, setPromoCode] = useState<PromoCode | null>(null);
  const [discount, setDiscount] = useState(0);

  const clearCart = () => {
    setSelectedPlan(null);
    setPromoCode(null);
    setDiscount(0);
    localStorage.removeItem("selectedPlan");
    localStorage.removeItem("promoCode");
    setIsProcessingPayment(false);
  };

  const applyPromoCode = async (code: string) => {
    if (!selectedPlan) {
      return { success: false, message: "No hay un plan seleccionado" };
    }

    try {
      // Check if promo code exists and is valid
      const currentDate = new Date().toISOString();

      const { data: promo, error } = await supabase
        .from("promo_codes")
        .select("*")
        .eq("code", code.toUpperCase())
        .eq("is_active", true)
        .gte("end_date", currentDate)
        .lte("start_date", currentDate)
        .single();

      if (error) {
        console.error("Error fetching promo code:", error);
        return {
          success: false,
          message: "Error al validar el código promocional",
        };
      }

      if (!promo) {
        return {
          success: false,
          message: "Código promocional inválido o expirado",
        };
      }

      // Check if promo code has reached max uses
      if (promo.max_uses !== null && promo.current_uses >= promo.max_uses) {
        return { success: false, message: "Código promocional agotado" };
      }

      // Check minimum purchase amount
      if (
        promo.min_purchase_amount !== null &&
        selectedPlan.price < promo.min_purchase_amount
      ) {
        return {
          success: false,
          message: `El monto mínimo para este código es $${promo.min_purchase_amount.toLocaleString()}`,
        };
      }

      // Check if user has already used this promo code
      if (user) {
        const { data: existingUse, error: useError } = await supabase
          .from("promo_code_uses")
          .select("*")
          .eq("promo_code_id", promo.id)
          .eq("user_id", user.id)
          .single();

        if (useError && useError.code !== "PGRST116") {
          // PGRST116 is "no rows returned"
          console.error("Error checking promo code usage:", useError);
          return {
            success: false,
            message: "Error al validar el uso del código",
          };
        }

        if (existingUse) {
          return {
            success: false,
            message: "Ya has usado este código promocional",
          };
        }
      }

      // Calculate discount
      let newDiscount = 0;
      if (promo.discount_type === "percentage") {
        newDiscount = (selectedPlan.price * promo.discount) / 100;
      } else {
        newDiscount = promo.discount;
      }

      // Ensure discount doesn't exceed plan price
      newDiscount = Math.min(newDiscount, selectedPlan.price);

      setPromoCode(promo);
      setDiscount(newDiscount);
      localStorage.setItem("promoCode", JSON.stringify(promo));

      return {
        success: true,
        message: "Código promocional aplicado correctamente",
      };
    } catch (error) {
      console.error("Error applying promo code:", error);
      return {
        success: false,
        message: "Error al aplicar el código promocional",
      };
    }
  };

  const removePromoCode = () => {
    setPromoCode(null);
    setDiscount(0);
    localStorage.removeItem("promoCode");
  };

  const initiatePayment = async () => {
    if (!selectedPlan || !user || isProcessingPayment) return;

    setIsProcessingPayment(true);

    try {
      // Create order record
      const { data: order, error: orderError } = await supabase
        .from("orders")
        .insert({
          user_id: user.id,
          plan_name: selectedPlan.name,
          amount: selectedPlan.price,
          discount: discount,
          final_amount:
            selectedPlan.price + selectedPlan.price * 0.19 - discount,
          status: "pending",
        })
        .select()
        .single();

      if (orderError) throw orderError;

      // Record promo code usage if applicable
      if (promoCode) {
        const { error: promoError } = await supabase
          .from("promo_code_uses")
          .insert({
            promo_code_id: promoCode.id,
            user_id: user.id,
            order_id: order.id,
          });

        if (promoError) throw promoError;

        // Update current_uses count
        await supabase
          .from("promo_codes")
          .update({ current_uses: promoCode.current_uses + 1 })
          .eq("id", promoCode.id);
      }

      const reference = `ORDER_${order.id}`;
      const finalAmount = Math.round(
        (selectedPlan.price + selectedPlan.price * 0.19 - discount) * 100
      );

      // Remove any existing Wompi form
      const existingForm = document.getElementById("wompi-form");
      if (existingForm) {
        existingForm.remove();
      }

      // Create the form
      const form = document.createElement("form");
      form.id = "wompi-form";
      form.action = "https://checkout.wompi.co/p/";
      form.method = "GET";

      // Add the necessary fields
      const fields = {
        "public-key": "pub_test_ljPKa973x3TWlGkvXhlCr7Iy9GdXKYXl",
        currency: "COP",
        "amount-in-cents": finalAmount.toString(),
        reference: reference,
        "redirect-url": `${window.location.origin}/confirmacion-pago`,
      };

      // Add fields to form
      Object.entries(fields).forEach(([key, value]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = key;
        input.value = value;
        form.appendChild(input);
      });

      // Add submit button
      const submit = document.createElement("button");
      submit.type = "submit";
      submit.className =
        "w-full py-3 px-4 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-lg font-semibold transition-colors";
      submit.textContent = "PAGAR CON WOMPI";
      form.appendChild(submit);

      // Add form to container
      const container = document.getElementById("wompi-button-container");
      if (container) {
        container.innerHTML = "";
        container.appendChild(form);
      }

      setIsProcessingPayment(false);
      return { success: true };
    } catch (error) {
      console.error("Error initiating payment:", error);
      setIsProcessingPayment(false);
      return { success: false, error };
    }
  };

  return (
    <CartContext.Provider
      value={{
        selectedPlan,
        setSelectedPlan,
        clearCart,
        initiatePayment,
        isProcessingPayment,
        applyPromoCode,
        removePromoCode,
        promoCode,
        discount,
      }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};

export default CartContext;
